import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaTimes } from "react-icons/fa";
import { toast } from "react-toastify";
import { INDUSTRY_TYPE_MAP } from "../../../constants/industryTypeMap";
import { useProductDetailsMaster } from "../../../hooks/useProductDetailsMaster";
import StorageService from "../../../services/StorageService";
import p2pProcurementServiceService from "../../../services/p2pProcurementService.service";
import { buildLoginPath } from "../../../utils/authRedirect";
import { buildProcurementRequestReturnPath } from "../../../utils/procurementRequestRedirect";
import { savePasRequestDraft } from "../../../utils/pasRequestDraft";

const FREQUENCY_OPTIONS = [
  { value: "one_time", label: "One-time" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "ongoing", label: "Ongoing" },
];

const BUDGET_OPTIONS = [
  { value: "under_5l", label: "Under ₹5 lakh" },
  { value: "5l_25l", label: "₹5 lakh – ₹25 lakh" },
  { value: "25l_1cr", label: "₹25 lakh – ₹1 crore" },
  { value: "above_1cr", label: "Above ₹1 crore" },
];

const REQUEST_TYPE_BY_SERVICE = {
  supplier_discovery_reports: "supplier_discovery",
  managed_procurement_services: "managed_procurement",
  procurement_retainer_services: "procurement_retainer",
};

const MODAL_TITLE_BY_SERVICE = {
  supplier_discovery_reports: "Request Supplier Discovery Report",
  managed_procurement_services: "Request Managed Procurement Services",
  procurement_retainer_services: "Book Retainer Consultation",
};

const EMPTY_UI_FORM = {
  company_name: "",
  contact_name: "",
  email: "",
  phone: "",
  industry: "",
  product_requirement: "",
  quantity: "",
  quantity_unit: "",
  delivery_timeline: "",
  procurement_frequency: "",
  budget_range: "",
  procurement_type: "domestic",
  additional_notes: "",
};

const resolveRequesterOrganizationId = (organizations) => {
  if (!Array.isArray(organizations) || organizations.length === 0) return null;
  return Number(organizations[0].id);
};

const formatQuantityVolume = (quantity, unitLabel) => {
  const qty = String(quantity || "").trim();
  const unit = String(unitLabel || "").trim();
  if (!qty && !unit) return null;
  if (!unit) return qty;
  if (!qty) return unit;
  return `${qty} ${unit}`;
};

const buildInitialForm = (draftForm = null) => {
  const user = StorageService.getUser() || {};
  const base = {
    ...EMPTY_UI_FORM,
    company_name: user.company_name || "",
    contact_name: user.name || "",
    email: user.email || "",
    phone: user.phone || "",
  };
  if (!draftForm || typeof draftForm !== "object") return base;
  return { ...base, ...draftForm };
};

const extractMessage = (err, fallback) =>
  err?.response?.data?.message || err?.message || fallback;

const ServiceRequestModal = ({
  open,
  service,
  detail,
  organizations,
  onClose,
  onSubmitted,
  publicMode = false,
  initialForm = null,
  overlayClassName = "",
}) => {
  const navigate = useNavigate();
  const { attributeUnitOptions, loading: uomLoading } = useProductDetailsMaster();
  const [form, setForm] = useState(EMPTY_UI_FORM);
  const [submitting, setSubmitting] = useState(false);
  const isAuthenticated = StorageService.isAuthenticated();

  const unitOptions = useMemo(
    () => (Array.isArray(attributeUnitOptions) ? attributeUnitOptions : []),
    [attributeUnitOptions],
  );

  const selectedUnitLabel = useMemo(() => {
    if (!form.quantity_unit) return "";
    const match = unitOptions.find(
      (opt) => String(opt.value) === String(form.quantity_unit),
    );
    return match?.label || form.quantity_unit;
  }, [form.quantity_unit, unitOptions]);

  const modalTitle = useMemo(() => {
    if (!service) return "Request service";
    return (
      MODAL_TITLE_BY_SERVICE[service.service_code] ||
      `Request ${service.service_name}`
    );
  }, [service]);

  const requestType = useMemo(() => {
    if (!service) return "managed_procurement";
    return REQUEST_TYPE_BY_SERVICE[service.service_code] || "managed_procurement";
  }, [service]);

  useEffect(() => {
    if (!open) return undefined;
    setForm(buildInitialForm(initialForm));
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose, initialForm]);

  const requesterOrganizationId = useMemo(
    () => resolveRequesterOrganizationId(organizations),
    [organizations],
  );

  const setField = useCallback((name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!service?.id || !detail) {
      toast.warn("Service details are still loading");
      return;
    }
    if (!requesterOrganizationId) {
      toast.warn("No organisation is linked to your account for this request");
      return;
    }
    if (!form.company_name.trim()) {
      toast.warn("Company name is required");
      return;
    }
    if (!form.contact_name.trim()) {
      toast.warn("Contact name is required");
      return;
    }
    if (!form.email.trim()) {
      toast.warn("Email is required");
      return;
    }
    if (!form.phone.trim()) {
      toast.warn("Phone is required");
      return;
    }
    if (!form.industry) {
      toast.warn("Industry is required");
      return;
    }
    if (!form.product_requirement.trim()) {
      toast.warn("Product / material requirement is required");
      return;
    }

    if (publicMode && !isAuthenticated) {
      savePasRequestDraft(service.service_code, form);
      toast.info("Sign in to submit your request — your details will be kept.");
      navigate(
        buildLoginPath(buildProcurementRequestReturnPath(service.service_code)),
      );
      onClose();
      return;
    }

    const pricing = detail.pricings?.[0] || service?.active_pricing?.[0] || null;
    const notesPayload = {
      company_name: form.company_name.trim(),
      contact_name: form.contact_name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      industry: form.industry,
      quantity: form.quantity.trim() || null,
      quantity_unit: form.quantity_unit || null,
      quantity_volume: formatQuantityVolume(form.quantity, selectedUnitLabel),
      delivery_timeline: form.delivery_timeline.trim() || null,
      procurement_frequency: form.procurement_frequency || null,
      budget_range: form.budget_range || null,
      procurement_type: form.procurement_type,
      additional_notes: form.additional_notes.trim() || null,
      service_code: service.service_code,
      form_version: "pas_catalogue_modal_v1",
    };

    setSubmitting(true);
    try {
      await p2pProcurementServiceService.createRequest({
        service_id: Number(service.id),
        request_type: requestType,
        requester_organization_id: requesterOrganizationId,
        pricing_id: pricing?.id ? Number(pricing.id) : null,
        requirement_title: `${modalTitle} — ${form.company_name.trim()}`.slice(0, 255),
        requirement_description: form.product_requirement.trim(),
        currency: pricing?.currency || "INR",
        notes: JSON.stringify(notesPayload),
        create_transaction: true,
      });
      toast.success("Service request submitted");
      onSubmitted?.();
      onClose();
    } catch (err) {
      toast.error(extractMessage(err, "Failed to submit service request"));
    } finally {
      setSubmitting(false);
    }
  };

  if (!open || !service) return null;

  const overlayClasses = [
    "p2p-pas-modal-overlay",
    publicMode ? "p2p-pas-modal-overlay--public" : "",
    overlayClassName,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={overlayClasses}
      role="presentation"
      onClick={onClose}
    >
      <div
        className={`p2p-pas-modal${publicMode ? " p2p-pas-modal--public" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="p2p-pas-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p2p-pas-modal__header">
          <div className="p2p-pas-modal__header-text">
            <h2 id="p2p-pas-modal-title">{modalTitle}</h2>
            <p>Fill in your requirements and our team will get in touch</p>
          </div>
          <button
            type="button"
            className="p2p-pas-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            <FaTimes />
          </button>
        </div>

        <form className="p2p-pas-modal__form" onSubmit={handleSubmit}>
          <div className="p2p-pas-modal__body">
            <div className="p2p-pas-modal__grid">
              <label className="p2p-pas-modal__field">
                <span>
                  Company Name <span className="p2p-pas-modal__req">*</span>
                </span>
                <input
                  type="text"
                  value={form.company_name}
                  onChange={(e) => setField("company_name", e.target.value)}
                  placeholder="ABC Manufacturing Ltd."
                  required
                  maxLength={255}
                />
              </label>

              <label className="p2p-pas-modal__field">
                <span>
                  Contact Name <span className="p2p-pas-modal__req">*</span>
                </span>
                <input
                  type="text"
                  value={form.contact_name}
                  onChange={(e) => setField("contact_name", e.target.value)}
                  placeholder="John Doe"
                  required
                  maxLength={255}
                />
              </label>

              <label className="p2p-pas-modal__field">
                <span>
                  Email <span className="p2p-pas-modal__req">*</span>
                </span>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setField("email", e.target.value)}
                  placeholder="john.doe@abcmfg.com"
                  required
                  maxLength={255}
                />
              </label>

              <label className="p2p-pas-modal__field">
                <span>
                  Phone <span className="p2p-pas-modal__req">*</span>
                </span>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setField("phone", e.target.value)}
                  placeholder="+91 98765 43210"
                  required
                  maxLength={32}
                />
              </label>

              <label className="p2p-pas-modal__field p2p-pas-modal__field--full">
                <span>
                  Industry <span className="p2p-pas-modal__req">*</span>
                </span>
                <select
                  value={form.industry}
                  onChange={(e) => setField("industry", e.target.value)}
                  required
                >
                  <option value="">Select industry</option>
                  {INDUSTRY_TYPE_MAP.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </label>

              <label className="p2p-pas-modal__field p2p-pas-modal__field--full">
                <span>
                  Product/Material Requirement{" "}
                  <span className="p2p-pas-modal__req">*</span>
                </span>
                <textarea
                  value={form.product_requirement}
                  onChange={(e) => setField("product_requirement", e.target.value)}
                  placeholder="Describe what you need to procure..."
                  required
                  maxLength={4000}
                  rows={4}
                />
              </label>

              <label className="p2p-pas-modal__field">
                <span>Quantity/Volume</span>
                <div className="p2p-pas-modal__qty-row">
                  <input
                    type="text"
                    inputMode="decimal"
                    value={form.quantity}
                    onChange={(e) => setField("quantity", e.target.value)}
                    placeholder="e.g., 1000"
                    maxLength={64}
                  />
                  <select
                    value={form.quantity_unit}
                    onChange={(e) => setField("quantity_unit", e.target.value)}
                    disabled={uomLoading}
                    aria-label="Quantity unit"
                  >
                    <option value="">
                      {uomLoading ? "Loading units…" : "Unit"}
                    </option>
                    {unitOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </label>

              <label className="p2p-pas-modal__field">
                <span>Target Delivery Timeline</span>
                <input
                  type="text"
                  value={form.delivery_timeline}
                  onChange={(e) => setField("delivery_timeline", e.target.value)}
                  placeholder="e.g., 30 days, Q2 2026"
                  maxLength={255}
                />
              </label>

              <label className="p2p-pas-modal__field">
                <span>Procurement Frequency</span>
                <select
                  value={form.procurement_frequency}
                  onChange={(e) =>
                    setField("procurement_frequency", e.target.value)
                  }
                >
                  <option value="">Select frequency</option>
                  {FREQUENCY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </label>

              <label className="p2p-pas-modal__field">
                <span>Budget Range</span>
                <select
                  value={form.budget_range}
                  onChange={(e) => setField("budget_range", e.target.value)}
                >
                  <option value="">Select budget range</option>
                  {BUDGET_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </label>

              <fieldset className="p2p-pas-modal__field p2p-pas-modal__field--full p2p-pas-modal__radios">
                <legend>Procurement Type</legend>
                <div className="p2p-pas-modal__radio-row">
                  {[
                    { value: "domestic", label: "Domestic" },
                    { value: "international", label: "International" },
                    { value: "both", label: "Both" },
                  ].map((opt) => (
                    <label key={opt.value} className="p2p-pas-modal__radio">
                      <input
                        type="radio"
                        name="procurement_type"
                        value={opt.value}
                        checked={form.procurement_type === opt.value}
                        onChange={(e) =>
                          setField("procurement_type", e.target.value)
                        }
                      />
                      <span>{opt.label}</span>
                    </label>
                  ))}
                </div>
              </fieldset>

              <label className="p2p-pas-modal__field p2p-pas-modal__field--full">
                <span>Additional Notes</span>
                <textarea
                  value={form.additional_notes}
                  onChange={(e) => setField("additional_notes", e.target.value)}
                  placeholder="Any specific requirements or preferences..."
                  maxLength={4000}
                  rows={3}
                />
              </label>
            </div>
          </div>

          <div className="p2p-pas-modal__footer">
            <button
              type="submit"
              className="p2p-pas-modal__btn p2p-pas-modal__btn--primary"
              disabled={submitting}
            >
              {submitting ? "Submitting…" : "Submit Request"}
            </button>
            <button
              type="button"
              className="p2p-pas-modal__btn p2p-pas-modal__btn--secondary"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ServiceRequestModal;
